import React, {ChangeEvent, ReactNode, useCallback, useMemo, useRef, useState} from "react"
import {TFADescription} from "../TFADescription"
import {VerificationWarning} from "../VerificationWarning"
import styles from "./AuthPageTemplate.module.scss"
import {ResetRequest} from "../../../api/authAPI"
import {AuthHeader} from "../AuthHeader/AuthHeader"
import {APIStatus} from "scholastic-client-components/src/types/APITypes"
import {Button} from "scholastic-client-components/src/ui/Button/Button"
import {Box} from "scholastic-client-components/src/ui/Box/Box"
import {Input} from "scholastic-client-components/src/ui/Input/Input"
import {Trans, useTranslation} from "react-i18next"
import {authSlice} from "scholastic-client-components/src/slices/authSlice"
import {useDispatch} from "react-redux"
import {passwordValidationRules} from "scholastic-client-components/src/features/person"
import {composeValidators} from "scholastic-client-components/src/features/ReduxForm/validators"

export enum AuthStep {
    Login,
    ResetPassword,
    SuccessResetPassword,
    CreatePassword,
}

export interface AuthPageProps {
    heading: ReactNode
    onLogin: (arg: {email: string; password: string; code?: string}) => void
    loading?: boolean
    error?: string
    step?: AuthStep
    setStep?: (step: AuthStep) => void
    onReset?: (payload: ResetRequest) => void
    loadingReset?: boolean
    errorReset?: string
    resetSecondsLeft?: number
    savePasswordStatus: APIStatus
    onPasswordSave: (password: string) => void
    onChangeInput?: () => void
    navigateToLogin?: () => void
    initialEmail?: string
    verificationWarning?: {
        nonConfirmedAccount?: boolean
        loginStatus: APIStatus
        recoveryStatus: APIStatus
        resendStatus: APIStatus
        resendConfirmEmail: (email: string) => void
        timeLeft: number
    }
    tfa: {
        request2fa?: boolean
        resendTFA: (email: string) => void
        timeLeft: number
        loader: boolean
    }
    policies?: ReactNode
    onBackToLogin: () => void
}

export const AuthPageTemplate: React.FC<AuthPageProps> = ({
    heading,
    onLogin,
    loading,
    error,
    step = AuthStep.Login,
    setStep,
    onReset,
    errorReset,
    loadingReset,
    savePasswordStatus,
    onPasswordSave,
    onChangeInput,
    navigateToLogin = () => {
        window.location.pathname = "/login"
    },
    initialEmail = "",
    verificationWarning,
    policies,
    tfa,
    onBackToLogin,
    resetSecondsLeft,
}) => {
    const dispatch = useDispatch()
    const {t} = useTranslation(["person"])
    const [email, setEmail] = useState<string>(initialEmail)
    const [password, setPassword] = useState<string>("")
    const [passwordTouched, setPasswordTouched] = useState(false)
    const [repeatPasswordTouched, setRepeatPasswordTouched] = useState(false)
    const [repeatPassword, setRepeatPassword] = useState<string>("")
    const [code, setCode] = useState<string>("")
    const [codeTouched, setCodeTouched] = useState(false)
    const codeError = !code ? t`common:validation.required` : undefined
    const changeCode = (code) => {
        setCode(code)
        setCodeTouched(true)
    }

    const changePassword = (value: string) => {
        setPassword(value)
        setPasswordTouched(true)
    }

    const changeRepeatPassword = (value: string) => {
        setRepeatPassword(value)
        setRepeatPasswordTouched(true)
    }

    const handleAuth = useCallback(
        (e: {preventDefault: () => void}) => {
            e.preventDefault()
            if (step === AuthStep.CreatePassword) {
                onPasswordSave(password)
                return
            }

            if (email && password) {
                if (tfa?.request2fa && codeError) {
                    setCodeTouched(true)
                    return
                } else {
                    onLogin({email, password, code})
                }
            } else {
                if (onReset) {
                    onReset({email})
                }
            }
        },
        [code, email, onLogin, onPasswordSave, onReset, password, step, tfa, codeError]
    )

    const handleResetPassword = () => {
        if (setStep) {
            setStep(AuthStep.ResetPassword)
        }
        setEmail("")
        setPassword("")
    }

    const handleBackToLogin = () => {
        setEmail("")
        setPassword("")
        setCode("")
        onBackToLogin()
    }

    const loginFieldRef = useRef<HTMLInputElement | null>(null)
    const passwordFieldRef = useRef<HTMLInputElement | null>(null)
    const repeatPasswordFieldRef = useRef<HTMLInputElement | null>(null)

    const getSelectionOfCurrentField = (targetId: string) => {
        const ref =
            targetId === loginFieldRef.current?.id
                ? loginFieldRef
                : targetId === passwordFieldRef.current?.id
                  ? passwordFieldRef
                  : repeatPasswordFieldRef
        const text = ref.current as HTMLInputElement
        return text.value.substring(
            text.selectionStart ? text.selectionStart : 0,
            text.selectionEnd ? text.selectionEnd : 0
        )
    }

    const handlePaste = (
        e: React.ClipboardEvent<HTMLInputElement>,
        setValue: (value: string) => void
    ) => {
        e.preventDefault()
        if (!e.clipboardData) return
        setPasswordTouched(true)
        setRepeatPasswordTouched(true)
        const target = e.currentTarget
        const value = target.value
        const posStart = target.selectionStart ? target.selectionStart : undefined
        const stringWithoutSpaces = e.clipboardData.getData("text/plain").trim()
        const selection = getSelectionOfCurrentField(target.id)
        if (selection === value) {
            setValue(stringWithoutSpaces)
        } else {
            setValue(
                value
                    ? value.slice(0, posStart) + stringWithoutSpaces + value.slice(posStart)
                    : stringWithoutSpaces
            )
        }
    }

    const handleEmailChange = (value: string) => {
        if (value !== email && verificationWarning?.nonConfirmedAccount) {
            dispatch(authSlice.actions.resetVerificationWarnings())
        }
        setEmail(value)
    }

    const repeatPasswordError = useMemo(() => {
        return composeValidators(passwordValidationRules.repeatRequired())(repeatPassword, {
            password,
        })
    }, [password, repeatPassword])

    const passwordError = useMemo(() => {
        return composeValidators(passwordValidationRules.create)(password)
    }, [password])

    return (
        <div className={styles["auth-page-template"]}>
            <form className={styles["auth-container"]} onSubmit={handleAuth}>
                <AuthHeader text={heading} onLogoClick={navigateToLogin} mb={1.875} />
                {step === AuthStep.SuccessResetPassword ? (
                    <>
                        <Box width={17.5625}>
                            <Trans
                                i18nKey={"auth_portal:auth_page.password_reset_prompt"}
                                components={{
                                    lbr: <br />,
                                }}
                            />
                        </Box>
                        <Box mt={1.875} width={15} height={2}>
                            <Button
                                primary
                                width100percent
                                style={{minHeight: "2rem"}}
                                onClick={handleBackToLogin}
                            >
                                {t("auth_portal:auth_page.back_to_login_button")}
                            </Button>
                        </Box>
                    </>
                ) : step === AuthStep.CreatePassword ? (
                    savePasswordStatus === APIStatus.Success ? (
                        <>
                            <span>{t("auth_portal:auth_page.password_saved")}</span>
                            <Box mt={2} width={16.25} height={2}>
                                <Button
                                    primary
                                    width100percent
                                    style={{minHeight: "2rem"}}
                                    onClick={navigateToLogin}
                                >
                                    {t("auth_portal:auth_page.login_button")}
                                </Button>
                            </Box>
                        </>
                    ) : (
                        <>
                            <Input
                                ref={passwordFieldRef}
                                className={styles["password"]}
                                name="password"
                                label={t("person:fieldname.new_password")}
                                onChange={(
                                    e: ChangeEvent<HTMLInputElement> &
                                        ChangeEvent<HTMLTextAreaElement>
                                ) => {
                                    changePassword(e.currentTarget.value)
                                }}
                                onPaste={(e) => handlePaste(e, setPassword)}
                                value={password}
                                type="password"
                                error={passwordTouched ? passwordError : undefined}
                                autoComplete={"new-password"}
                            />
                            <Input
                                ref={repeatPasswordFieldRef}
                                className={styles["password"]}
                                name="repeatPassword"
                                label={t("person:fieldname.repeat_password")}
                                onChange={(
                                    e: ChangeEvent<HTMLInputElement> &
                                        ChangeEvent<HTMLTextAreaElement>
                                ) => {
                                    changeRepeatPassword(e.currentTarget.value)
                                }}
                                onPaste={(e) => handlePaste(e, setRepeatPassword)}
                                value={repeatPassword}
                                type="password"
                                conditionsIsVisible={false}
                                error={repeatPasswordTouched ? repeatPasswordError : undefined}
                                autoComplete={"new-password"}
                            />
                            <Button
                                className={styles["submit"]}
                                loadingMessage={t("auth_portal:auth_page.saving")}
                                loader={savePasswordStatus === APIStatus.Loading}
                                type="submit"
                                disabled={!!repeatPasswordError || !!passwordError}
                                primary
                                autoFocus
                            >
                                {t("auth_portal:auth_page.create_password_button")}
                            </Button>
                        </>
                    )
                ) : (
                    <>
                        {(verificationWarning?.loginStatus === APIStatus.Success ||
                            verificationWarning?.recoveryStatus === APIStatus.Success) &&
                            verificationWarning?.nonConfirmedAccount && (
                                <div className={styles.verificationWarning}>
                                    <VerificationWarning
                                        loader={
                                            verificationWarning.resendStatus === APIStatus.Loading
                                        }
                                        resendConfirmEmail={() =>
                                            verificationWarning.resendConfirmEmail(email)
                                        }
                                        timeLeft={verificationWarning.timeLeft}
                                    />
                                </div>
                            )}
                        {tfa?.request2fa && (
                            <div className={styles.tfaDescription}>
                                <TFADescription
                                    resendTFA={() => tfa.resendTFA(email)}
                                    timeLeft={tfa.timeLeft}
                                    loader={tfa.loader}
                                />
                            </div>
                        )}

                        {!tfa?.request2fa && (
                            <Input
                                ref={loginFieldRef}
                                className={styles["email"]}
                                name="email"
                                label={t("person:fieldname.email")}
                                data-test={"email-input"}
                                onChange={(
                                    e: ChangeEvent<HTMLInputElement> &
                                        ChangeEvent<HTMLTextAreaElement>
                                ) => {
                                    onChangeInput && onChangeInput()
                                    handleEmailChange(e.currentTarget.value)
                                }}
                                onPaste={(e) => handlePaste(e, handleEmailChange)}
                                value={email}
                                required
                                error={step === AuthStep.ResetPassword && errorReset}
                            />
                        )}
                        {step === AuthStep.Login && !tfa?.request2fa && (
                            <div className={styles["password-container"]}>
                                <Input
                                    ref={passwordFieldRef}
                                    className={styles["password"]}
                                    name="password"
                                    label={t("person:fieldname.password")}
                                    data-test={"password-input"}
                                    conditionsIsVisible={false}
                                    onChange={(
                                        e: ChangeEvent<HTMLInputElement> &
                                            ChangeEvent<HTMLTextAreaElement>
                                    ) => {
                                        onChangeInput && onChangeInput()
                                        setPassword(e.currentTarget.value)
                                    }}
                                    onPaste={(e) => handlePaste(e, setPassword)}
                                    value={password}
                                    type="password"
                                    required
                                    error={error}
                                />
                            </div>
                        )}
                        {tfa?.request2fa && (
                            <Input
                                className={styles.code}
                                name={"code"}
                                label={t("auth_portal:auth_page.fieldname.2fa_code")}
                                data-test="tfa-code-input"
                                onChange={(
                                    e: ChangeEvent<HTMLInputElement> &
                                        ChangeEvent<HTMLTextAreaElement>
                                ) => {
                                    onChangeInput && onChangeInput()
                                    changeCode(e.currentTarget.value)
                                }}
                                value={code}
                                error={(codeTouched ? codeError : "") || error}
                            />
                        )}
                        <Button
                            className={styles.submit}
                            loadingMessage={
                                step === AuthStep.Login
                                    ? t("auth_portal:auth_page.login_progress")
                                    : t("auth_portal:auth_page.reset_progress")
                            }
                            loader={loading || loadingReset}
                            disabled={!!resetSecondsLeft}
                            type="submit"
                            primary
                            autoFocus
                            data-test={
                                step === AuthStep.Login ? "login-button" : "reset-password-button"
                            }
                        >
                            {step === AuthStep.Login
                                ? t("auth_portal:auth_page.login_button")
                                : t("auth_portal:auth_page.reset_password_button", {count: resetSecondsLeft})}
                        </Button>
                        {step === AuthStep.Login && !tfa.request2fa && (
                            <div className={styles.policies}>{policies}</div>
                        )}
                        {step === AuthStep.Login && !tfa?.request2fa && (
                            <Box mt={1.25}>
                                <Button onClick={handleResetPassword} text>
                                    {t("auth_portal:auth_page.recover_password_button")}
                                </Button>
                            </Box>
                        )}
                        {((step === AuthStep.Login && tfa?.request2fa) ||
                            step === AuthStep.ResetPassword) && (
                            <Box mt={1.25}>
                                <Button text onClick={handleBackToLogin}>
                                    {t("auth_portal:auth_page.back_to_login_button")}
                                </Button>
                            </Box>
                        )}
                    </>
                )}
            </form>
        </div>
    )
}
